import {Component, effect, ElementRef, Input, signal, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiCompatibilityService, ApiCompatibilityState} from '../api/api-compatibility.service';
import {ServerErrorDialogService} from './server-error-dialog.service';
import {AuthService} from '../../auth/auth.service';
import {catchError, distinctUntilChanged, map, of, pairwise, startWith, switchMap} from 'rxjs';
import {Capacitor} from '@capacitor/core';
import {ExternalBrowserService} from '../external-browser.service';

@Component({
  selector: 'sm-server-error-dialog-host',
  templateUrl: './server-error-dialog-host.component.html',
  styleUrls: ['./server-error-dialog-host.component.scss'],
  standalone: false,
})
export class ServerErrorDialogHostComponent {
  readonly canSeeDetails$;
  @ViewChild('errorDetails') private errorDetails?: ElementRef<HTMLDetailsElement>;
  private readonly compatibilityScoped = signal(false);

  @Input() set compatibilityState(state: ApiCompatibilityState | undefined) {
    this.compatibilityScoped.set(!!state);
    this.serverErrorDialogService.syncCompatibilityFailure(state);
  }

  constructor(readonly serverErrorDialogService: ServerErrorDialogService, auth: AuthService,
    readonly compatibility: ApiCompatibilityService, private readonly externalBrowser: ExternalBrowserService) {
    serverErrorDialogService.isOpen$.pipe(takeUntilDestroyed()).subscribe(() => this.collapseDetails());
    effect(() => {
      // A fast probe can finish between render passes with the same input state.
      // Track recovery signals too, so its HTTP outcome still reaches the host.
      this.compatibility.recoveryVisible();
      this.compatibility.serverUnavailable();
      const state = this.compatibility.state();
      this.serverErrorDialogService.syncCompatibilityFailure(
        this.compatibilityScoped() || this.compatibility.recoveryRequested() ? state : undefined,
      );
    });
    const userId$ = auth.session$.pipe(map(session => session?.user.id), distinctUntilChanged());
    userId$.pipe(pairwise(), takeUntilDestroyed()).subscribe(() => {
      this.collapseDetails();
      this.serverErrorDialogService.details = '';
    });
    this.canSeeDetails$ = userId$.pipe(
      switchMap(id => id ? auth.getCurrentUser().pipe(
        map(user => user.roleCode === 'Administrator'), catchError(() => of(false)), startWith(false),
      ) : of(false)),
    );
  }

  get isOpen$() {
    return this.serverErrorDialogService.isOpen$;
  }

  get canOpenWebVersion(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
      && this.serverErrorDialogService.isConfirmedIncompatibility;
  }

  async handleOpenWebVersion(event?: Event): Promise<void> {
    event?.preventDefault();
    if (this.canOpenWebVersion) await this.externalBrowser.open('https://invemory.com');
  }

  private collapseDetails(): void {
    if (this.errorDetails) this.errorDetails.nativeElement.open = false;
  }

  handleClose(): void {
    this.serverErrorDialogService.close();
  }

  handleConfirm(): void {
    if (this.serverErrorDialogService.canRetry) this.serverErrorDialogService.retry();
    else this.serverErrorDialogService.close();
  }
}
